import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateItemInput, ItemKind, UpdateItemInput } from "@csf/shared";
import { api } from "./client.js";
import type {
  Granularity,
  HistoryPointDto,
  ItemDto,
  JobDto,
  MeDto,
  TagCountDto,
} from "./types.js";

// ─── me ─────────────────────────────────────────────────────────────────────
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeDto>("/api/me"),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── items list ─────────────────────────────────────────────────────────────
export function useItems(
  params: { kind?: ItemKind; isActive?: boolean; tag?: string } = {},
) {
  return useQuery({
    queryKey: ["items", params],
    queryFn: () =>
      api<{ items: ItemDto[] }>("/api/items", {
        query: {
          kind: params.kind,
          isActive: params.isActive,
          tag: params.tag,
        },
      }),
    select: (r) => r.items,
    staleTime: 30 * 1000,
  });
}

export function useTagsWithCounts() {
  return useQuery({
    queryKey: ["item-tags"],
    queryFn: () => api<{ tags: TagCountDto[] }>("/api/items/tags"),
    select: (r) => r.tags,
    staleTime: 60 * 1000,
  });
}

// EffectiveCron — backend резолвит cron для item (override / tag / default / conflict).
// Используется Detail для отображения источника расписания.
export interface EffectiveCronDto {
  cron: string;
  source: "override" | "tag" | "default" | "conflict";
  tag?: string;
  conflictingTags?: string[];
}

export function useItem(id: string | undefined) {
  return useQuery({
    queryKey: ["items", id],
    queryFn: () =>
      api<{ item: ItemDto; effectiveCron: EffectiveCronDto }>(`/api/items/${id}`),
    enabled: !!id,
    // Не select — нам нужны и item, и effectiveCron в Detail.
  });
}

export function useItemHistory(
  id: string | undefined,
  params: {
    from?: string;
    to?: string;
    granularity?: Granularity;
    // Только для стикеров: 1..5 → серия по числу копий вместо sum.
    multiplicity?: 1 | 2 | 3 | 4 | 5;
  } = {},
) {
  return useQuery({
    queryKey: ["items", id, "history", params],
    queryFn: () =>
      api<{ points: HistoryPointDto[] }>(`/api/items/${id}/history`, {
        query: {
          from: params.from,
          to: params.to,
          granularity: params.granularity ?? "day",
          multiplicity: params.multiplicity,
        },
      }),
    enabled: !!id,
    select: (r) => r.points,
  });
}

// ─── mutations ──────────────────────────────────────────────────────────────
export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateItemInput) =>
      api<{ item: ItemDto; existed: boolean }>("/api/items", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useUpdateItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateItemInput) =>
      api<{ item: ItemDto }>(`/api/items/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/items/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useRefreshItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ enqueued: boolean }>(`/api/items/${id}/refresh`, { method: "POST" }),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: ["items", id] });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

// Ручное добавление прошлой точки истории — на случай когда юзер знает supply
// в конкретную дату из других источников и хочет дозаполнить график.
export function useBackfillItem(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { takenAt: string; count: number; note?: string }) =>
      api<{ snapshot: { id: string; updated: boolean } }>(
        `/api/items/${itemId}/backfill`,
        { method: "POST", body: input },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["items", itemId] });
    },
  });
}

// ─── jobs ───────────────────────────────────────────────────────────────────
export function useJobs(itemId: string | undefined, limit = 20) {
  return useQuery({
    queryKey: ["jobs", { itemId, limit }],
    queryFn: () =>
      api<{ jobs: JobDto[] }>("/api/jobs", {
        query: { itemId, limit },
      }),
    enabled: !!itemId,
    select: (r) => r.jobs,
    refetchInterval: 10_000,
  });
}
